import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import LogoSVG from '../components/LogoSVG';

export default function ProjectList() {
  const navigate = useNavigate();
  const { managerId } = useParams();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [managerName, setManagerName] = useState(location.state?.managerName || '');
  const [sliderValue, setSliderValue] = useState(3);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [addHovered, setAddHovered] = useState(false);
  const editInputRef = useRef(null);
  const addInputRef = useRef(null);

  useEffect(() => { fetchProjects(); }, [managerId]);

  // 목록 자동 갱신 (3초)
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`/api/projectlist/index.php?manager_id=${managerId}`, { withCredentials: true });
        setProjects(res.data);
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [managerId]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`/api/projectlist/index.php?manager_id=${managerId}`, { withCredentials: true });
      setProjects(res.data);
      if (res.data[0]?.manager_name) {
        setManagerName(res.data[0].manager_name);
      } else {
        const ws = await axios.get('/api/workspace/index.php', { withCredentials: true });
        const m = ws.data.find(m => m.id === Number(managerId));
        if (m) setManagerName(m.name);
      }
    } catch { navigate('/login'); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    await axios.post('/api/projectlist/index.php', { manager_id: Number(managerId), name: newName.trim() }, { withCredentials: true });
    setNewName(''); setAddModal(false); fetchProjects();
  };

  const handleRename = async (id) => {
    if (!editName.trim()) { setEditId(null); return; }
    await axios.put('/api/projectlist/index.php', { id, name: editName.trim() }, { withCredentials: true });
    setEditId(null); fetchProjects();
  };

  const handleDelete = async (id) => {
    await axios.delete('/api/projectlist/index.php', { data: { id }, withCredentials: true });
    setDeleteConfirm(null); fetchProjects();
  };

  const iconSize = 48 + sliderValue * 10;
  const cardSize = 180 + sliderValue * 24;

  return (
    <div style={s.wrap}>
      {/* 상단바 */}
      <div style={s.topbar}>
        <LogoSVG size={32} />
        <div style={s.divider} />
        <div style={s.topbarPath}>
          <span style={s.topbarWorkspace} onClick={() => navigate('/')}>WORKSPACE</span>
          <span style={s.topbarArrow}>
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 1 6 6 1 11"/></svg>
          </span>
          <span style={{ ...s.topbarCurrent, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
            {managerName}
          </span>
        </div>
      </div>

      {/* 컨트롤 */}
      <div style={s.controlRow}>
        <span style={s.sectionLabel}>프로젝트 {projects.length}개</span>
        <div style={s.sliderGroup}>
          <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.max(1, v - 1))}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <input type="range" min={1} max={5} value={sliderValue}
            onChange={e => setSliderValue(Number(e.target.value))} style={s.slider} />
          <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.min(5, v + 1))}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z"/></svg>
          </button>
        </div>
      </div>

      {/* 그리드 */}
      <div style={s.grid}>
        {projects.map(p => (
          <div key={p.id}
            style={{ ...s.projectCard, width: cardSize, height: cardSize, borderColor: hoveredId === p.id ? '#5755df' : 'rgba(255,255,255,0.2)', background: hoveredId === p.id ? 'rgba(87,85,223,0.07)' : 'transparent', transform: hoveredId === p.id ? 'translateY(-2px)' : 'none', transition: 'border-color .18s, background .18s, transform .18s' }}
            onMouseEnter={() => setHoveredId(p.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => navigate(`/videos/${p.id}`, { state: { projectName: p.name, managerName, managerId: Number(managerId) } })}>
            <button style={s.deleteBtn} onClick={e => { e.stopPropagation(); setDeleteConfirm(p); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21.1,5.2h-4.6v-2.3c0-1.3-1-2.3-2.3-2.3h-4.6c-1.3,0-2.3,1-2.3,2.3v2.3H2.9c-.6,0-1.1.5-1.1,1.1s.5,1.1,1.1,1.1h0l1.1,12.6c0,1.9,1.5,3.4,3.4,3.4h9.1c1.9,0,3.4-1.5,3.4-3.3l1.1-12.7h0c.6,0,1.1-.5,1.1-1.1s-.5-1.1-1.1-1.1ZM9.7,2.9h4.6v2.3h-4.6v-2.3ZM17.7,20c0,.6-.5,1.1-1.1,1.1H7.4c-.6,0-1.1-.5-1.1-1.2l-1-12.5h13.5l-1,12.6Z"/>
                <path d="M9.7,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
                <path d="M14.3,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
              </svg>
            </button>
            <div style={s.cardIcon}>
              <svg style={{ width: iconSize, height: iconSize }} viewBox="0 0 24 24" fill="none" stroke="#afafaf" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 9a2 2 0 0 1 2-2h4l2 2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9z"/>
              </svg>
            </div>
            <div style={s.cardName}>
              {editId === p.id ? (
                <input ref={editInputRef} style={s.nameInput} value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={() => handleRename(p.id)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRename(p.id); if (e.key === 'Escape') setEditId(null); }}
                  onClick={e => e.stopPropagation()} />
              ) : (
                <>
                  <span style={s.cardNameSpan}>{p.name}</span>
                  <button style={s.pencilBtn} onClick={e => { e.stopPropagation(); setEditId(p.id); setEditName(p.name); setTimeout(() => editInputRef.current?.focus(), 50); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
            <div style={s.cardInfo}>영상 {p.video_count || 0}개</div>
            {p.has_new_feedback
              ? <span style={s.badgeNew}>새 피드백</span>
              : <span style={s.badgePlaceholder} />}
          </div>
        ))}

        {/* 추가 카드 */}
        <div style={{ ...s.addCard, width: cardSize, height: cardSize, background: addHovered ? 'rgba(87,85,223,0.07)' : 'transparent', color: addHovered ? '#a5a3ff' : 'rgba(255,255,255,0.45)', transform: addHovered ? 'translateY(-2px)' : 'none', transition: 'background .18s, color .18s, transform .18s', position: 'relative', border: 'none' }}
          onMouseEnter={() => setAddHovered(true)}
          onMouseLeave={() => setAddHovered(false)}
          onClick={() => { setAddModal(true); setTimeout(() => addInputRef.current?.focus(), 50); }}>
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" rx="15" ry="15"
              fill="none"
              stroke={addHovered ? '#5755df' : 'rgba(255,255,255,0.2)'}
              strokeWidth="1.4"
              strokeDasharray="6.8 6.8"
              style={{ transition: 'stroke .18s' }}
            />
          </svg>
          <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round">
            <path d="M12 5 L12 19 M5 12 L19 12"/>
          </svg>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={s.delBox} onClick={e => e.stopPropagation()}>
            <div style={s.delTitle}>휴지통으로 이동</div>
            <div style={s.delDesc}><strong style={{ color: '#fff' }}>{deleteConfirm.name}</strong>을(를) 휴지통으로 이동합니다.</div>
            <div style={s.delActions}>
              <button style={s.delBtn} onClick={() => setDeleteConfirm(null)}>취소</button>
              <button style={{ ...s.delBtn, ...s.delBtnDanger }} onClick={() => handleDelete(deleteConfirm.id)}>이동</button>
            </div>
          </div>
        </div>
      )}

      {/* 추가 모달 */}
      {addModal && (
        <div style={s.overlay} onClick={() => { setAddModal(false); setNewName(''); }}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>프로젝트 추가</div>
            <div style={s.modalLabel}>프로젝트 이름</div>
            <input ref={addInputRef} style={s.modalInput} type="text" placeholder="프로젝트 이름을 입력하세요"
              value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAddModal(false); setNewName(''); } }} />
            <div style={s.modalActions}>
              <button style={s.modalBtn} onClick={() => { setAddModal(false); setNewName(''); }}>취소</button>
              <button style={{ ...s.modalBtn, ...s.modalBtnPrimary }} onClick={handleAdd}>추가</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', background: '#191919', fontFamily: "'Pretendard', sans-serif", padding: 20, boxSizing: 'border-box' },
  topbar: { background: 'transparent', borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '0 18px', height: 56, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 19 },
  divider: { width: 1, height: 18, background: 'rgba(255,255,255,0.15)', flexShrink: 0 },
  topbarPath: { display: 'flex', alignItems: 'center', gap: 16 },
  topbarWorkspace: { fontSize: '13pt', fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px', cursor: 'pointer' },
  topbarArrow: { color: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', flexShrink: 0 },
  topbarCurrent: { fontSize: '13pt', fontWeight: 400, color: '#fff', letterSpacing: '0.5px' },
  controlRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 17 },
  sectionLabel: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  sliderGroup: { display: 'flex', alignItems: 'center', gap: 10, height: 24 },
  sliderBtn: { color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' },
  slider: { width: 90, height: 2, cursor: 'pointer' },
  grid: { display: 'flex', flexWrap: 'wrap', gap: 38, justifyContent: 'flex-start' },
  projectCard: { background: 'transparent', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 16, padding: '48px 18px 22px', position: 'relative', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'center', boxSizing: 'border-box' },
  deleteBtn: { position: 'absolute', top: 12, right: 12, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0 },
  cardIcon: { minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardName: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 36 },
  cardNameSpan: { fontSize: '15pt', fontWeight: 400, color: '#fff' },
  pencilBtn: { width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)', padding: 0, flexShrink: 0 },
  nameInput: { flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid #5755df', borderRadius: 6, padding: '2px 7px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit' },
  cardInfo: { fontSize: 11, color: 'rgba(255,255,255,0.3)', minHeight: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  badgeNew: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(87,85,223,0.2)', color: '#a5a3ff', border: '1px solid rgba(87,85,223,0.3)', minHeight: 22 },
  badgePlaceholder: { minHeight: 22, display: 'block' },
  addCard: { borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxSizing: 'border-box' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  delBox: { background: '#303030', border: '0.56px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '28px 24px', width: 320, boxSizing: 'border-box' },
  delTitle: { fontSize: '13pt', fontWeight: 600, color: '#fff', marginBottom: 10 },
  delDesc: { fontSize: '10pt', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, marginBottom: 22 },
  delActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  delBtn: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  delBtnDanger: { background: '#e74c3c', color: '#fff', borderColor: '#e74c3c' },
  modalBox: { background: '#303030', border: '0.56px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '28px 24px', width: 340, boxShadow: '0 24px 60px rgba(0,0,0,0.5)', boxSizing: 'border-box' },
  modalTitle: { fontSize: '13pt', fontWeight: 600, color: '#fff', marginBottom: 18 },
  modalLabel: { fontSize: '10pt', color: 'rgba(255,255,255,0.4)', marginBottom: 6 },
  modalInput: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  modalBtn: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  modalBtnPrimary: { background: '#5755df', color: '#fff', borderColor: '#5755df' },
};
